import { motion } from "motion/react";
import {
  Sparkles,
  Zap,
  Crown,
  Check,
  ArrowRight,
  Coins,
  Clock,
  Download,
  Star,
  Gift,
  Infinity,
  Shield,
  Clock3,
  Package,
  Plus,
  Minus,
} from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "@/src/lib/utils";
import { useState } from "react";

interface TokenPackageCardProps {
  key?: string | number;
  name: string;
  description: string;
  price: string;
  tokenAmount: number;
  bonusTokens: number;
  features: string[];
  popular?: boolean;
  gradient: string;
  icon: ReactNode;
  savings?: string;
}

function TokenPackageCard({
  name,
  description,
  price,
  tokenAmount,
  bonusTokens,
  features,
  popular,
  gradient,
  icon,
  savings,
}: TokenPackageCardProps) {
  const totalTokens = tokenAmount + bonusTokens;
  const pricePerToken = Math.round(parseInt(price.replace(/\D/g, '')) / totalTokens);
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn(
        "relative rounded-3xl p-6 lg:p-8 h-full flex flex-col",
        popular
          ? "bg-gradient-to-b from-white/10 to-white/5 border-2 border-purple-500 scale-105"
          : "bg-white/5 border border-white/10",
      )}
    >
      {popular && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full text-xs font-bold uppercase tracking-wider">
          Paling Populer
        </div>
      )}

      <div className="flex items-center gap-4 mb-6">
        <div
          className={cn(
            "w-14 h-14 rounded-2xl flex items-center justify-center",
            gradient,
          )}
        >
          {icon}
        </div>
        <div>
          <h3 className="text-2xl font-bold">{name}</h3>
          <p className="text-sm text-white/50">{description}</p>
        </div>
      </div>

      <div className="mb-6">
        <div className="flex items-baseline gap-2">
          <span className="text-4xl lg:text-5xl font-bold">{price}</span>
        </div>
        {savings && (
          <p className="text-sm text-green-400 mt-2 flex items-center gap-1">
            <Gift className="w-4 h-4" />
            Hemat {savings}
          </p>
        )}
        <p className="text-sm text-white/40 mt-1">
          ~Rp {pricePerToken.toLocaleString('id-ID')}/token
        </p>
      </div>

      <div className="py-4 px-4 rounded-xl bg-white/5 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-white/70">
            <Coins className="w-5 h-5 text-yellow-400" />
            <span className="font-semibold">{totalTokens} Token</span>
          </div>
          {bonusTokens > 0 && (
            <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded-full">
              +{bonusTokens} Bonus
            </span>
          )}
        </div>
        <p className="text-xs text-white/40 mt-1">
          1 Token = 1 kali generate
        </p>
      </div>

      <ul className="space-y-3 mb-8 flex-1">
        {features.map((feature, i) => (
          <li key={i} className="flex items-start gap-3 text-sm">
            <Check className="w-5 h-5 text-green-400 shrink-0 mt-0.5" />
            <span className="text-white/70">{feature}</span>
          </li>
        ))}
      </ul>

      <button
        className={cn(
          "w-full py-4 rounded-xl font-semibold transition-all flex items-center justify-center gap-2",
          popular
            ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:opacity-90"
            : "bg-white/10 hover:bg-white/20 text-white",
        )}
      >
        Beli Paket
        <ArrowRight className="w-4 h-4" />
      </button>
    </motion.div>
  );
}

function SingleTokenCard() {
  const [quantity, setQuantity] = useState(1);
  const unitPrice = 2500;
  const totalPrice = unitPrice * quantity;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-3xl p-6 lg:p-8 bg-gradient-to-b from-white/5 to-white/[0.02] border border-white/10 h-full flex flex-col"
    >
      <div className="flex items-center gap-4 mb-6">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-gray-500 to-gray-600 flex items-center justify-center">
          <Coins className="w-7 h-7 text-white" />
        </div>
        <div>
          <h3 className="text-2xl font-bold">Beli Satuan</h3>
          <p className="text-sm text-white/50">Fleksibel, beli sesuai kebutuhan</p>
        </div>
      </div>

      <div className="mb-6">
        <div className="flex items-baseline gap-2">
          <span className="text-4xl lg:text-5xl font-bold">
            Rp {unitPrice.toLocaleString('id-ID')}
          </span>
          <span className="text-white/50">/token</span>
        </div>
      </div>

      <div className="py-4 px-4 rounded-xl bg-white/5 mb-6">
        <label className="text-sm text-white/50 mb-2 block">Jumlah Token</label>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setQuantity(Math.max(1, quantity - 1))}
            className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
          >
            <Minus className="w-4 h-4" />
          </button>
          <input
            type="number"
            value={quantity}
            onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
            className="flex-1 bg-white/5 border border-white/10 rounded-xl py-2 px-4 text-center font-semibold"
            min="1"
          />
          <button
            onClick={() => setQuantity(quantity + 1)}
            className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="py-4 px-4 rounded-xl bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 mb-6">
        <div className="flex justify-between items-center">
          <span className="text-white/70">Total</span>
          <span className="text-xl font-bold">
            Rp {totalPrice.toLocaleString('id-ID')}
          </span>
        </div>
        <p className="text-xs text-white/40 mt-1">
          Dapatkan {quantity} kali generate
        </p>
      </div>

      <ul className="space-y-3 mb-8 flex-1">
        <li className="flex items-start gap-3 text-sm">
          <Check className="w-5 h-5 text-green-400 shrink-0 mt-0.5" />
          <span className="text-white/70">Tidak expire</span>
        </li>
        <li className="flex items-start gap-3 text-sm">
          <Check className="w-5 h-5 text-green-400 shrink-0 mt-0.5" />
          <span className="text-white/70">Resolusi hingga 4K</span>
        </li>
        <li className="flex items-start gap-3 text-sm">
          <Check className="w-5 h-5 text-green-400 shrink-0 mt-0.5" />
          <span className="text-white/70">Support via chat</span>
        </li>
      </ul>

      <button
        className="w-full py-4 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white"
      >
        Beli Token
        <ArrowRight className="w-4 h-4" />
      </button>
    </motion.div>
  );
}

function BenefitCard({
  icon,
  title,
  description,
}: {
  icon: ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/5 hover:bg-white/[0.04] transition-colors">
      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500/20 to-purple-500/20 flex items-center justify-center mb-4">
        {icon}
      </div>
      <h3 className="font-semibold mb-2">{title}</h3>
      <p className="text-sm text-white/50">{description}</p>
    </div>
  );
}

export function PricingPage() {
  const benefits = [
    {
      icon: <Coins className="w-6 h-6 text-yellow-400" />,
      title: "Sistem Token",
      description: "1 Token = 1 kali generate. Simpel dan transparan, tidak ada biaya tersembunyi.",
    },
    {
      icon: <Zap className="w-6 h-6 text-blue-400" />,
      title: "Proses Cepat",
      description: "Generate foto dalam hitungan detik, tidak perlu menunggu lama",
    },
    {
      icon: <Shield className="w-6 h-6 text-purple-400" />,
      title: "Kualitas HD",
      description: "Hasil colorize hingga resolusi 4K dengan detail sempurna",
    },
    {
      icon: <Download className="w-6 h-6 text-green-400" />,
      title: "Download Bebas",
      description: "Unduh hasil kapan saja tanpa batasan watermark",
    },
    {
      icon: <Clock3 className="w-6 h-6 text-orange-400" />,
      title: "Akses 24/7",
      description: "Layanan online kapan saja, tidak perlu antri",
    },
    {
      icon: <Infinity className="w-6 h-6 text-pink-400" />,
      title: "Token Tidak Expire",
      description: "Token yang kamu beli tidak expired, gunakan kapan pun kamu mau",
    },
  ];

  const tokenPackages = [
    {
      name: "10 Token",
      description: "Paket kecil untuk coba-coba",
      price: "Rp 20.000",
      tokenAmount: 10,
      bonusTokens: 0,
      features: [
        "10 kali generate",
        "Token tidak expire",
        "Resolusi hingga 4K",
        "Download tanpa watermark",
        "Support via chat",
      ],
      gradient: "bg-gradient-to-br from-blue-500 to-cyan-500",
      icon: <Package className="w-7 h-7 text-white" />,
    },
    {
      name: "50 Token",
      description: "Paling populer - Hemat 20%",
      price: "Rp 80.000",
      tokenAmount: 50,
      bonusTokens: 5,
      features: [
        "55 kali generate",
        "Bonus 5 token gratis",
        "Token tidak expire",
        "Resolusi hingga 4K",
        "Priority support",
        "Akses semua model AI",
      ],
      popular: true,
      gradient: "bg-gradient-to-br from-purple-500 to-pink-500",
      icon: <Crown className="w-7 h-7 text-white" />,
      savings: "Rp 15.000",
    },
    {
      name: "100 Token",
      description: "Paket hemat untuk content creator",
      price: "Rp 140.000",
      tokenAmount: 100,
      bonusTokens: 15,
      features: [
        "115 kali generate",
        "Bonus 15 token gratis",
        "Token tidak expire",
        "Resolusi hingga 4K",
        "Priority support",
        "Akses semua model AI",
        "Early access fitur baru",
      ],
      gradient: "bg-gradient-to-br from-amber-500 to-orange-500",
      icon: <Sparkles className="w-7 h-7 text-white" />,
      savings: "Rp 45.000",
    },
  ];

  const testimonials = [
    {
      name: "Sarah Wijaya",
      role: "Photographer",
      content:
        "Sistem tokennya sangat simpel! 1 token 1 generate, tidak ada yang ribet. Hasilnya juga luar biasa!",
      rating: 5,
    },
    {
      name: "Budi Santoso",
      role: "Content Creator",
      content:
        "Paket 50 token paling worth it. Dapat bonus 5 token, jadi bisa colorize banyak foto untuk konten.",
      rating: 5,
    },
    {
      name: "Maya Putri",
      role: "Archivist",
      content:
        "Beli token satuan pas butuh 2-3 foto aja. Fleksibel banget, tidak perlu beli paket besar.",
      rating: 5,
    },
  ];

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-500/10 via-purple-500/5 to-transparent" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl" />

        <div className="relative max-w-5xl mx-auto px-6 py-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 mb-8">
              <Coins className="w-4 h-4 text-yellow-400" />
              <span className="text-sm font-medium">
                Sistem Token Baru
              </span>
            </div>

            <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
              1 Token = 1 Generate
              <br />
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-500">
                Simpel & Transparan
              </span>
            </h1>

            <p className="text-lg text-white/60 max-w-2xl mx-auto mb-12">
              Tidak ada biaya berlangganan bulanan. Beli token sekali, gunakan kapan saja. 
              Token tidak expired dan bebas download hasilnya.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#token-packages"
                className="px-8 py-4 rounded-2xl bg-gradient-to-r from-blue-500 to-purple-600 font-bold hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
              >
                <Coins className="w-5 h-5" />
                Lihat Paket Token
              </a>
              <a
                href="#single-token"
                className="px-8 py-4 rounded-2xl bg-white/10 border border-white/20 font-bold hover:bg-white/20 transition-colors flex items-center justify-center gap-2"
              >
                Beli Satuan
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 border-t border-white/5">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-2xl lg:text-3xl font-bold mb-4">
              Kenapa Sistem Token?
            </h2>
            <p className="text-white/60">
              Keuntungan menggunakan sistem token kami
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
              >
                <BenefitCard {...benefit} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Token Packages Section */}
      <section id="token-packages" className="py-20 border-t border-white/5">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              Pilih Paket Token
            </h2>
            <p className="text-white/60 max-w-xl mx-auto">
              Beli paket lebih hemat dengan bonus token. Semua token tidak expire.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 items-start">
            {tokenPackages.map((pkg) => (
              <TokenPackageCard 
                key={pkg.name}
                name={pkg.name} 
                description={pkg.description} 
                price={pkg.price} 
                tokenAmount={pkg.tokenAmount} 
                bonusTokens={pkg.bonusTokens} 
                features={pkg.features} 
                gradient={pkg.gradient} 
                icon={pkg.icon} 
                popular={pkg.popular} 
                savings={pkg.savings} 
              />
            ))}
          </div>
        </div>
      </section>

      {/* Single Token Section */}
      <section id="single-token" className="py-20 border-t border-white/5">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              Atau Beli Satuan
            </h2>
            <p className="text-white/60 max-w-xl mx-auto">
              Butuh token dikit saja? Beli satuan sesuai kebutuhanmu.
            </p>
          </div>

          <div className="max-w-md mx-auto">
            <SingleTokenCard />
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-20 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-2xl lg:text-3xl font-bold mb-4">
              Perbandingan Paket
            </h2>
            <p className="text-white/60">
              Lihat fitur apa saja yang tersedia di setiap paket
            </p>
          </div>

          <div className="rounded-2xl overflow-hidden border border-white/10">
            <div className="grid grid-cols-5 gap-4 px-6 py-4 bg-white/5 text-sm font-semibold">
              <span className="text-white/60">Fitur</span>
              <span className="text-center">Satuan</span>
              <span className="text-center">10 Token</span>
              <span className="text-center text-purple-400">50 Token</span>
              <span className="text-center text-amber-400">100 Token</span>
            </div>
            {[
              { feature: "Jumlah Generate", satuan: "1-1000", t10: "10", t50: "55", t100: "115" },
              { feature: "Token Bonus", satuan: "-", t10: "-", t50: "5", t100: "15" },
              { feature: "Harga/Token", satuan: "Rp 2.500", t10: "Rp 2.000", t50: "Rp 1.454", t100: "Rp 1.217" },
              { feature: "Resolusi 4K", satuan: "✓", t10: "✓", t50: "✓", t100: "✓" },
              { feature: "No Watermark", satuan: "✓", t10: "✓", t50: "✓", t100: "✓" },
              { feature: "Semua Model AI", satuan: "-", t10: "-", t50: "✓", t100: "✓" },
              { feature: "Support", satuan: "Chat", t10: "Chat", t50: "Priority", t100: "Priority" },
              { feature: "Early Access", satuan: "-", t10: "-", t50: "-", t100: "✓" },
            ].map((row, i) => (
              <div
                key={i}
                className="grid grid-cols-5 gap-4 px-6 py-4 border-t border-white/5 hover:bg-white/[0.02] transition-colors"
              >
                <span className="text-white/70">{row.feature}</span>
                <span className="text-center text-white/60">{row.satuan}</span>
                <span className="text-center text-white/60">{row.t10}</span>
                <span className="text-center text-white/80">{row.t50}</span>
                <span className="text-center text-white/80">{row.t100}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 border-t border-white/5">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              Kata Mereka
            </h2>
            <p className="text-white/60">Testimoni dari pengguna kami</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="p-6 rounded-2xl bg-white/[0.02] border border-white/5"
              >
                <div className="flex gap-1 mb-4">
                  {Array.from({ length: testimonial.rating }).map((_, j) => (
                    <Star
                      key={j}
                      className="w-4 h-4 fill-yellow-400 text-yellow-400"
                    />
                  ))}
                </div>
                <p className="text-white/70 mb-4">"{testimonial.content}"</p>
                <div>
                  <p className="font-semibold">{testimonial.name}</p>
                  <p className="text-sm text-white/50">{testimonial.role}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 border-t border-white/5">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-2xl lg:text-3xl font-bold mb-4">
              Pertanyaan Umum
            </h2>
          </div>

          <div className="space-y-4">
            {[
              {
                q: "Apakah token bisa expired?",
                a: "Tidak, token yang kamu beli tidak memiliki masa expired. Kamu bisa menggunakannya kapan saja sesuai kebutuhan.",
              },
              {
                q: "Berapa token yang dibutuhkan untuk 1 generate?",
                a: "1 Token digunakan untuk 1 kali generate. Setiap kali kamu colorize foto, akan menggunakan 1 token.",
              },
              {
                q: "Apakah bisa beli token satuan?",
                a: "Ya! Kamu bisa beli token satuan mulai dari 1 token dengan harga Rp 2.500/token.",
              },
              {
                q: "Apakah ada bonus token?",
                a: "Ya! Jika kamu beli paket 50 token, dapat bonus 5 token. Beli paket 100 token, dapat bonus 15 token.",
              },
              {
                q: "Bagaimana cara cek sisa token?",
                a: "Sisa token bisa dilihat di halaman profil atau dashboard setelah login.",
              },
              {
                q: "Apakah token bisa refund?",
                a: "Token yang sudah dibeli tidak dapat di-refund, tetapi tidak akan expire jadi bisa digunakan kapan saja.",
              },
            ].map((faq, i) => (
              <div key={i} className="p-6 rounded-2xl bg-white/[0.02] border border-white/5">
                <h3 className="font-semibold mb-2">{faq.q}</h3>
                <p className="text-sm text-white/60">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 border-t border-white/5">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Siap Mengubah Foto Lawasmu?
          </h2>
          <p className="text-white/60 mb-8">
            Beli token dan mulai colorize foto-foto berhargamu sekarang.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="#token-packages"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl bg-gradient-to-r from-blue-500 to-purple-600 font-bold hover:opacity-90 transition-opacity"
            >
              <Package className="w-5 h-5" />
              Beli Paket Token
            </a>
            <a
              href="#single-token"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl bg-white/10 border border-white/20 font-bold hover:bg-white/20 transition-colors"
            >
              <Coins className="w-5 h-5" />
              Beli Satuan
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-12">
        <div className="max-w-5xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-6 text-white/40 text-sm">
          <p>© 2026 Ritsu. All rights reserved.</p>
          <div className="flex items-center gap-8">
            <a href="#" className="hover:text-white transition-colors">
              Privacy Policy
            </a>
            <a href="#" className="hover:text-white transition-colors">
              Terms of Service
            </a>
            <a href="#" className="hover:text-white transition-colors">
              Contact
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
